<template>
  <v-sheet color="bgtopology" class="defaultCard">
    <!-- 1. HOSTS -->
    <div class="mb-6">
      <div class="d-flex align-center mb-2">
        <h3 class="text-h6 font-weight-bold">1. Estado de Servidores</h3>
        <span v-if="ultimaActualizacion" class="text-caption text-medium-emphasis ml-3 font-italic">
          ({{ ultimaActualizacion }})
        </span>
      </div>
      <v-data-table
        :headers="headersHosts"
        :items="hostsData"
        density="compact"
        class="elevation-1 cursor-pointer"
        no-data-text="No hay servidores disponibles"
        @click:row="(event, row) => abrirHistorico(row, 'hosts')"
      >
        <template #item.condicion="{ item }">
          <v-chip :color="getCondicionColor(item.condicion)" size="small" label>
            {{ item.condicion }}
          </v-chip>
        </template>
        <template #item.cpu_pct="{ item }">{{ item.cpu_pct }}%</template>
        <template #item.mem_pct="{ item }">{{ item.mem_pct }}%</template>
      </v-data-table>
    </div>

    <v-divider class="my-4" />

    <!-- 2. DATASTORES -->
    <div class="mb-6">
      <div class="d-flex align-center mb-2">
        <h3 class="text-h6 font-weight-bold">2. Datastores</h3>
        <span v-if="ultimaActualizacion" class="text-caption text-medium-emphasis ml-3 font-italic">
          ({{ ultimaActualizacion }})
        </span>
      </div>
      <v-data-table
        :headers="headersDatastores"
        :items="datastoresData"
        density="compact"
        class="elevation-1 cursor-pointer"
        no-data-text="No hay datastores disponibles"
        @click:row="(event, row) => abrirHistorico(row, 'datastores')"
      >
        <template #item.condicion="{ item }">
          <v-chip :color="getCondicionColor(item.condicion)" size="small" label>
            {{ item.condicion }}
          </v-chip>
        </template>
        <template #item.capacidad_tb="{ item }">{{ item.capacidad_tb }} TB</template>
        <template #item.libre_tb="{ item }">{{ item.libre_tb }} TB</template>
      </v-data-table>
    </div>

    <v-divider class="my-4" />

    <!-- 3. MÁQUINAS VIRTUALES (VMs) -->
    <div class="mb-6">
      <div class="d-flex align-center mb-2">
        <h3 class="text-h6 font-weight-bold">3. Máquinas Virtuales (VMs)</h3>
        <span v-if="ultimaActualizacion" class="text-caption text-medium-emphasis ml-3 font-italic">
          ({{ ultimaActualizacion }})
        </span>
      </div>
      <v-data-table
        :headers="headersVms"
        :items="vmsData"
        density="compact"
        class="elevation-1 cursor-pointer"
        no-data-text="No hay máquinas virtuales disponibles"
        @click:row="(event, row) => abrirHistorico(row, 'vms')"
      >
        <template #item.estado="{ item }">
          <v-chip :color="item.estado === 'Encendido' ? 'success' : 'grey'" size="small">
            {{ item.estado }}
          </v-chip>
        </template>
        <template #item.prov_gb="{ item }">{{ item.prov_gb }} GB</template>
        <template #item.usado_gb="{ item }">{{ item.usado_gb }} GB</template>
      </v-data-table>
    </div>

    <v-divider class="my-4" />

    <!-- 4. ALARMAS -->
    <div class="mb-2">
      <div class="d-flex align-center mb-2">
        <h3 class="text-h6 font-weight-bold text-error">4. Alarmas Activas</h3>
        <span v-if="ultimaActualizacion" class="text-caption text-medium-emphasis ml-3 font-italic">
          ({{ ultimaActualizacion }})
        </span>
      </div>
      <v-data-table
        :headers="headersAlarms"
        :items="alarmsData"
        density="compact"
        class="elevation-1 cursor-pointer"
        no-data-text="Sin alarmas activas"
      >
        <template #item.gravedad="{ item }">
          <v-chip :color="getGravedadColor(item.gravedad)" size="small" class="font-weight-bold">
            {{ item.gravedad }}
          </v-chip>
        </template>
      </v-data-table>
    </div>
  </v-sheet>

  <!-- MODAL DE HISTÓRICO -->
  <v-dialog v-model="dialogHistorico" max-width="850px">
    <v-card color="bgtopology">
      <v-card-title class="d-flex justify-space-between align-center">
        <span class="text-h6 font-weight-bold">
          Histórico: {{ equipoSeleccionado }}
        </span>
        <v-btn icon="mdi-close" variant="text" @click="dialogHistorico = false" />
      </v-card-title>

      <v-card-text>
        <!-- Selector de Métricas para VMs -->
        <div v-if="tipoTablaActual === 'vms'" class="d-flex justify-center mb-4">
          <v-btn-toggle
            v-model="metricaVmSeleccionada"
            mandatory
            color="primary"
            variant="outlined"
            density="comfortable"
            @update:model-value="actualizarGraficoVM"
          >
            <v-btn value="disco" prepend-icon="mdi-harddisk">Disco (GB)</v-btn>
            <v-btn value="cpu" prepend-icon="mdi-cpu-64-bit">CPU (MHz)</v-btn>
            <v-btn value="ram" prepend-icon="mdi-memory">RAM (MB)</v-btn>
          </v-btn-toggle>
        </div>

        <!-- Gráficos Estándar -->
        <highcharts :options="chartOptions" />
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import axios from 'axios';
import { Chart as highcharts } from 'highcharts-vue';

// Importación de constantes y utilidades
import { TOKEN } from '@/constants/labels';
import { APIS_SQLLITE_REPORT, TIMEOUTTIME } from '@/constants/apis';
import { showConsoleError } from '@/functions/errors';

// Estados reactivos
const dialogHistorico = ref(false);
const equipoSeleccionado = ref('');
const tipoTablaActual = ref('');
const metricaVmSeleccionada = ref<'disco' | 'cpu' | 'ram'>('disco');
const datosHistoricosGuardados = ref<any[]>([]);

const showError = ref(false);
const showServer = ref(false);

// Variable para la hora global de última extracción de la DB
const ultimaActualizacion = ref('');

// Datos de tablas
const extractionRunsData = ref([]);
const hostsData = ref([]);
const datastoresData = ref<any[]>([]);
const vmsData = ref([]);
const alarmsData = ref([]);

const configHeader = { 
  timeout: TIMEOUTTIME, 
  headers: { "Authorization": `Bearer ${TOKEN}` } 
};

// Configuración predeterminada del gráfico Estándar
const chartOptions = ref<any>({
  chart: { type: 'line', backgroundColor: 'transparent' },
  title: { text: '' },
  xAxis: { categories: [] },
  yAxis: { title: { text: 'Valor' } },
  series: []
});

// Headers para Vuetify Data Tables
const headersHosts = ref([
  { title: 'Nombre / IP', key: 'nombre' },
  { title: 'Estado', key: 'estado' },
  { title: 'Condición', key: 'condicion' },
  { title: 'Clúster', key: 'cluster' },
  { title: '% CPU', key: 'cpu_pct' },
  { title: '% Memoria', key: 'mem_pct' },
  { title: 'Estado HA', key: 'ha_estado' },
  { title: 'Uptime', key: 'uptime' }
]);

const headersDatastores = ref([
  { title: 'Nombre', key: 'nombre' },
  { title: 'Condición', key: 'condicion' },
  { title: 'Tipo', key: 'tipo' },
  { title: 'Capacidad (TB)', key: 'capacidad_tb' },
  { title: 'Libre (TB)', key: 'libre_tb' }
]);

const headersVms = ref([
  { title: 'Nombre VM', key: 'nombre' },
  { title: 'Estado', key: 'estado' },
  { title: 'Condición', key: 'condicion' },
  { title: 'Aprovisionado (GB)', key: 'prov_gb' },
  { title: 'Usado (GB)', key: 'usado_gb' },
  { title: 'CPU (MHz)', key: 'cpu_mhz' },
  { title: 'RAM (MB)', key: 'mem_mb' }
]);

const headersAlarms = ref([
  { title: 'Alarma', key: 'alarma' },
  { title: 'Objeto Afectado', key: 'objeto' },
  { title: 'Tipo de Objeto', key: 'tipo_objeto' },
  { title: 'Gravedad', key: 'gravedad' }
]);

/**
 * Función auxiliar para formatear la fecha a DD/MM HH:mm
 */
function formatearFechaHora(timestamp: string | Date) {
  if (!timestamp) return '';
  const d = new Date(timestamp);
  if (isNaN(d.getTime())) return String(timestamp);

  const dia = String(d.getDate()).padStart(2, '0');
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const hora = String(d.getHours()).padStart(2, '0');
  const minutos = String(d.getMinutes()).padStart(2, '0');
  return `${dia}/${mes} ${hora}:${minutos}`;
}

/**
 * Petición inicial de datos generales
 */
async function getAllServerData() {
  showError.value = false;
  showServer.value = false;

  try {
    const [resRuns, resHosts, resDatastores, resVms, resAlarms] = await Promise.all([
      axios.get(APIS_SQLLITE_REPORT.value.getExtractionRuns, configHeader),
      axios.get(APIS_SQLLITE_REPORT.value.getHosts, configHeader),
      axios.get(APIS_SQLLITE_REPORT.value.getDatastores, configHeader),
      axios.get(APIS_SQLLITE_REPORT.value.getVms, configHeader),
      axios.get(APIS_SQLLITE_REPORT.value.getAlarms, configHeader)
    ]);

    extractionRunsData.value = resRuns.data.data;
    hostsData.value = resHosts.data.data;
    datastoresData.value = resDatastores.data.data;
    vmsData.value = resVms.data.data;
    alarmsData.value = resAlarms.data.data;

    // Extrae la última hora de extracción registrada en la base de datos
    if (resRuns.data.data && resRuns.data.data.length > 0) {
      const ultimoRun = resRuns.data.data[0];
      const campoFecha = ultimoRun.timestamp || ultimoRun.created_at || ultimoRun.fecha;
      ultimaActualizacion.value = formatearFechaHora(campoFecha);
    }

    showServer.value = true;
  } catch (error) {
    showError.value = true;
    showServer.value = false;
    showConsoleError(error);
  }
}

/**
 * Consulta el histórico según la fila seleccionada
 */
async function abrirHistorico(row: any, tabla: string) {
  const dataFila = row.item ? row.item : row;
  const identificador = dataFila.nombre || dataFila.ip || dataFila.objeto;

  if (!identificador) return;

  equipoSeleccionado.value = identificador;
  tipoTablaActual.value = tabla;
  metricaVmSeleccionada.value = 'disco';

  try {
    const res = await axios.get(APIS_SQLLITE_REPORT.value.getHistorico, {
      params: { 
        tabla, 
        identificador,
        _t: new Date().getTime()
      },
      headers: { "Authorization": `Bearer ${TOKEN}` },
      timeout: TIMEOUTTIME
    });

    datosHistoricosGuardados.value = res.data.data || [];

    if (tabla === 'vms') {
      actualizarGraficoVM();
    } else {
      construirGraficoGeneral(tabla);
    }

    dialogHistorico.value = true;
  } catch (error) {
    showConsoleError(error);
  }
}

/**
 * Procesa categorías y series para Hosts y Datastores
 */
function construirGraficoGeneral(tabla: string) {
  const datos = datosHistoricosGuardados.value;

  const categoriasFechas = datos.map((item: any) => formatearFechaHora(item.timestamp));

  let series: any[] = [];
  let tituloY = '';

  if (tabla === 'hosts') {
    tituloY = 'Porcentaje (%)';
    series = [
      { name: '% CPU', data: datos.map((i) => i.cpu_pct), color: '#1E88E5' },
      { name: '% Memoria', data: datos.map((i) => i.mem_pct), color: '#FB8C00' }
    ];
  } else if (tabla === 'datastores') {
    tituloY = 'Terabytes (TB)';
    series = [
      { name: 'Capacidad Total (TB)', data: datos.map((i) => i.capacidad_tb), color: '#2196F3' },
      { name: 'Espacio Libre (TB)', data: datos.map((i) => i.libre_tb), color: '#4CAF50' }
    ];
  }

  chartOptions.value = {
    chart: { type: 'line', backgroundColor: 'transparent' },
    title: { text: `Rendimiento: ${equipoSeleccionado.value}` },
    xAxis: { categories: categoriasFechas },
    yAxis: { title: { text: tituloY } },
    series
  };
}

/**
 * Conmuta entre Disco, CPU y RAM cuando la tabla seleccionada es de Máquinas Virtuales
 */
function actualizarGraficoVM() {
  const datos = datosHistoricosGuardados.value;

  const categoriasFechas = datos.map((item: any) => formatearFechaHora(item.timestamp));

  let series: any[] = [];
  let tituloY = '';

  if (metricaVmSeleccionada.value === 'disco') {
    tituloY = 'Gigabytes (GB)';
    series = [
      { name: 'Aprovisionado (GB)', data: datos.map((i) => i.prov_gb), color: '#9C27B0' },
      { name: 'Usado (GB)', data: datos.map((i) => i.usado_gb), color: '#F44336' }
    ];
  } else if (metricaVmSeleccionada.value === 'cpu') {
    tituloY = 'Megahertz (MHz)';
    series = [
      { name: 'Uso CPU (MHz)', data: datos.map((i) => i.cpu_mhz), color: '#00BCD4' }
    ];
  } else if (metricaVmSeleccionada.value === 'ram') {
    tituloY = 'Megabytes (MB)';
    series = [
      { name: 'Uso RAM (MB)', data: datos.map((i) => i.mem_mb), color: '#E91E63' }
    ];
  }

  chartOptions.value = {
    chart: { type: 'line', backgroundColor: 'transparent' },
    title: { text: `VM: ${equipoSeleccionado.value}` },
    xAxis: { categories: categoriasFechas },
    yAxis: { title: { text: tituloY } },
    series
  };
}

// Helpers de estilo
const getCondicionColor = (condicion: string) => {
  if (!condicion) return 'default';
  const val = condicion.toLowerCase();
  if (val === 'normal') return 'success';
  if (val === 'crítico' || val === 'critico') return 'error';
  return 'warning';
};

const getGravedadColor = (gravedad: string) => {
  if (!gravedad) return 'default';
  const val = gravedad.toUpperCase();
  if (val === 'CRÍTICA' || val === 'CRITICA') return 'error';
  if (val === 'ADVERTENCIA' || val === 'WARNING') return 'warning';
  return 'info';
};

onMounted(() => {
  getAllServerData();
});
</script>

<style scoped>
.card-lte {
  z-index: 10;
}
</style>